import { CloseIcon } from 'assets/icons/CloseIcon'
import CalendarInput from 'components/CalendarInput/CalendarInput'
import { HonestSelectAsyncInput } from 'components/HonestAsyncSelect/HonestAsyncSelectInput'
import { Input } from 'components/Input/Input'
import { ToggleButton } from 'components/Input/ToggleButton'
import { Modal } from 'components/Modal/Modal'
import { ISelectValue } from 'constants/globalTypes'
import { recordFuelDeliveryBatch } from 'features/fuelManagement/fuelManagementSlice'
import useModalClose from 'hooks/useModalClose'
import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { AppDispatch } from 'store'
import { CursorPointer, Flex, ItemsGrid } from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { OtherText, TitleText } from 'styling/Typography'
import { IFuelDeliveryRequestLine } from 'types/api/fuelDeliveryRequestsApi.interface'
import { IRecordFuelDeliveryBatchFormState } from 'types/fuelDeliveryRequests.interface'
import { formatDateForBackend } from 'utils/helpers'
import {
  fuelDeliveryFuelTypeLabel,
} from '../fuelData'
import useFocusFirstInput from 'utils/useFocusFirstInput'

const parseUsers = (data: { data: { id: number; fullName: string }[] }) =>
  data.data.map((i) => ({ label: i.fullName, value: i.id }))

const parseNamed = (data: any) =>
  (data?.data || data || []).map((item: any) => ({ label: item.name, value: item.id }))

export const RecordFuelDeliveryBatchModal = ({
  requestId,
  line,
  closeModal,
  onSaved,
}: {
  requestId: number
  line: IFuelDeliveryRequestLine
  closeModal: () => void
  onSaved?: () => void
}) => {
  const dispatch = useDispatch<AppDispatch>()
  const [state, setState] = useState<IRecordFuelDeliveryBatchFormState>({
    amount: null,
    price: null,
    date: new Date(),
    supplier: null,
    waybillRef: '',
    waybillLitres: null,
    tankDipBeforeL: null,
    tankDipAfterL: null,
    sealNumbers: '',
    sealsIntact: true,
    verifiedBy: null,
    receivedBy: '',
    verificationNote: '',
  })
  useFocusFirstInput()
  useModalClose(closeModal)

  const handleChange = (e: any) => {
    const { name, value, valueAsNumber, type } = e.target
    setState((prev) => ({
      ...prev,
      [name]: type === 'number' ? (value === '' ? null : valueAsNumber) : value,
    }))
  }

  const canSave = state.amount != null && state.amount >= 0 && state.price != null && state.price >= 0 && state.date

  const handleSave = async () => {
    if (!canSave) return
    const seals = state.sealNumbers
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
    const resp = await dispatch(
      recordFuelDeliveryBatch({
        id: requestId,
        lineId: line.id,
        body: {
          amount: Number(state.amount),
          price: Number(state.price),
          date: formatDateForBackend(state.date),
          supplierId: state.supplier?.value ? Number(state.supplier.value) : undefined,
          waybillRef: state.waybillRef || undefined,
          waybillLitres: state.waybillLitres != null ? Number(state.waybillLitres) : undefined,
          tankDipBeforeL: state.tankDipBeforeL != null ? Number(state.tankDipBeforeL) : undefined,
          tankDipAfterL: state.tankDipAfterL != null ? Number(state.tankDipAfterL) : undefined,
          sealNumbers: seals.length ? seals : undefined,
          sealsIntact: state.sealsIntact,
          verifiedByUserId: state.verifiedBy?.value ? Number(state.verifiedBy.value) : undefined,
          receivedBy: state.receivedBy || undefined,
          verificationNote: state.verificationNote || undefined,
        },
      }),
    )
    if (resp.meta.requestStatus === 'fulfilled') {
      onSaved?.()
      closeModal()
    }
  }

  return (
    <Modal black width='640px'>
      <Flex column between center>
        <Flex column gap='1rem' width='100%'>
          <Flex between width='100%' center style={{ marginBottom: '1rem' }}>
            <TitleText weight='bold'>Record delivery</TitleText>
            <CursorPointer onClick={closeModal}>
              <CloseIcon />
            </CursorPointer>
          </Flex>
          <OtherText grey>
            {`${fuelDeliveryFuelTypeLabel(line.fuelType)} · ${line.quantity} ${line.unit?.name || ''} → ${line.fuelTank?.name || 'Tank'}`}
          </OtherText>
          <ItemsGrid columns={2} gap='1rem' colgap='1rem' width='100%'>
            <Input
              nomarg
              type='number'
              labelText='Amount (L)'
              name='amount'
              value={state.amount ?? ''}
              onChange={handleChange}
              required
            />
            <Input
              nomarg
              type='number'
              labelText='Price / L'
              name='price'
              value={state.price ?? ''}
              onChange={handleChange}
              required
            />
            <CalendarInput
              nomarg
              name='date'
              labelText='Date & time'
              defaultValue={state.date}
              maxDate={new Date()}
              includeTime
              onChange={(d: Date) => d && setState((prev) => ({ ...prev, date: d }))}
            />
            <HonestSelectAsyncInput
              nomarg
              link='fuel/management/supplier'
              name='supplier'
              labelText='Supplier (optional)'
              onChange={(v: ISelectValue) => setState((prev) => ({ ...prev, supplier: v }))}
              parserFunction={parseNamed}
            />
            <Input
              nomarg
              labelText='Waybill ref'
              name='waybillRef'
              value={state.waybillRef}
              onChange={handleChange}
            />
            <Input
              nomarg
              type='number'
              labelText='Waybill litres'
              name='waybillLitres'
              value={state.waybillLitres ?? ''}
              onChange={handleChange}
            />
            <Input
              nomarg
              type='number'
              labelText='Tank dip before (L)'
              name='tankDipBeforeL'
              value={state.tankDipBeforeL ?? ''}
              onChange={handleChange}
            />
            <Input
              nomarg
              type='number'
              labelText='Tank dip after (L)'
              name='tankDipAfterL'
              value={state.tankDipAfterL ?? ''}
              onChange={handleChange}
            />
          </ItemsGrid>
          <Input
            nomarg
            labelText='Seal numbers'
            name='sealNumbers'
            placeholder='e.g. S-4471, S-4472'
            value={state.sealNumbers}
            onChange={handleChange}
          />
          <ToggleButton
            start
            label='Seals intact'
            state={state.sealsIntact}
            handleChange={() =>
              setState((prev) => ({ ...prev, sealsIntact: !prev.sealsIntact }))
            }
          />
          <HonestSelectAsyncInput
            nomarg
            link='users/basic'
            name='verifiedBy'
            labelText='Verified by (optional)'
            onChange={(v: ISelectValue) => setState((prev) => ({ ...prev, verifiedBy: v }))}
            parserFunction={parseUsers}
          />
          <Input
            nomarg
            labelText='Received by'
            name='receivedBy'
            value={state.receivedBy}
            onChange={handleChange}
          />
          <Input
            nomarg
            labelText='Verification note'
            name='verificationNote'
            value={state.verificationNote}
            onChange={handleChange}
          />
        </Flex>
        <Button
          style={{ marginTop: '1rem' }}
          disabled={!canSave}
          variant='primary'
          onClick={handleSave}
        >
          Save batch
        </Button>
      </Flex>
    </Modal>
  )
}
